<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <EmployeeSidebar :sidebarOpen="sidebarOpen" currentPage="payslips" />

    <!-- Main Content -->
    <div
      class="main-content flex-grow-1"
      :class="{ 'sidebar-collapsed': !sidebarOpen }"
    >
      <!-- Navbar -->
      <EmployeeNavbar 
        :title="$t('employee.payslips.title')" 
        @toggleSidebar="toggleSidebar" 
        @logout="handleLogout" 
      />

      <!-- Content -->
      <div class="container-fluid p-4">
        <!-- Statistics Cards -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-success">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">Total Payslips</p>
                    <h3 class="mb-0 fw-bold">{{ payslips.length }}</h3>
                    <small class="text-muted">All time</small>
                  </div>
                  <div class="stat-icon bg-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-files"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M13 0H6a2 2 0 0 0-2 2 2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2 2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm0 13V4a2 2 0 0 0-2-2H5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1zM3 4a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-info">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">Latest Payment</p>
                    <h3 class="mb-0 fw-bold">
                      {{ formatCurrency(latestPayment) }}
                    </h3>
                    <small class="text-muted">{{ latestPaymentDate }}</small>
                  </div>
                  <div class="stat-icon bg-info">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-cash-coin"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M11 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm5-4a5 5 0 1 1-10 0 5 5 0 0 1 10 0z"
                      />
                      <path
                        d="M9.438 11.944c.047.596.518 1.06 1.363 1.116v.44h.375v-.443c.875-.061 1.386-.529 1.386-1.207 0-.618-.39-.936-1.09-1.1l-.296-.07v-1.2c.376.043.614.248.671.532h.658c-.047-.575-.54-1.024-1.329-1.073V8.5h-.375v.45c-.747.073-1.255.522-1.255 1.158 0 .562.378.92 1.007 1.066l.248.061v1.272c-.384-.058-.639-.27-.696-.563h-.668zm1.36-1.354c-.369-.085-.569-.26-.569-.522 0-.294.216-.514.572-.578v1.1h-.003zm.432.746c.449.104.655.272.655.569 0 .339-.257.571-.709.614v-1.195l.054.012z"
                      />
                      <path
                        d="M1 0a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h4.083c.058-.344.145-.678.258-1H3a2 2 0 0 0-2-2V3a2 2 0 0 0 2-2h10a2 2 0 0 0 2 2v3.528c.38.34.717.728 1 1.154V1a1 1 0 0 0-1-1H1z"
                      />
                      <path
                        d="M9.998 5.083 10 5a2 2 0 1 0-3.132 1.65 5.982 5.982 0 0 1 3.13-1.567z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-primary">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">YTD Earnings</p>
                    <h3 class="mb-0 fw-bold">
                      {{ formatCurrency(ytdEarnings) }}
                    </h3>
                    <small class="text-muted">Year 2026</small>
                  </div>
                  <div class="stat-icon bg-primary">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-graph-up-arrow"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M0 0h1v15h15v1H0V0Zm10 3.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V4.9l-3.613 4.417a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61L13.445 4H10.5a.5.5 0 0 1-.5-.5Z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Filter -->
        <div class="row mb-4">
          <div class="col-md-3">
            <select class="form-select" v-model="yearFilter">
              <option value="all">All Years</option>
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
            </select>
          </div>
        </div>

        <!-- Payslips Table -->
        <div class="card border-0 shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Period</th>
                    <th>Payment Date</th>
                    <th>Gross Pay</th>
                    <th>Deductions</th>
                    <th>Net Pay</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="payslip in filteredPayslips" :key="payslip.id">
                    <td class="fw-semibold">{{ payslip.period }}</td>
                    <td>{{ formatDate(payslip.paymentDate) }}</td>
                    <td>{{ formatCurrency(payslip.grossPay) }}</td>
                    <td class="text-danger">
                      -{{ formatCurrency(payslip.deductions) }}
                    </td>
                    <td class="fw-bold text-success">
                      {{ formatCurrency(payslip.netPay) }}
                    </td>
                    <td>
                      <button
                        class="btn btn-sm btn-outline-info me-2"
                        @click="viewDetails(payslip)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          class="bi bi-eye"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"
                          />
                          <path
                            d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
                          />
                        </svg>
                        View
                      </button>
                      <button
                        class="btn btn-sm btn-outline-primary"
                        @click="downloadPayslip(payslip)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          class="bi bi-download"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"
                          />
                          <path
                            d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"
                          />
                        </svg>
                        Download
                      </button>
                    </td>
                  </tr>
                  <tr v-if="filteredPayslips.length === 0">
                    <td colspan="6" class="text-center py-4 text-muted">
                      No payslips found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- View Details Modal -->
    <div
      class="modal fade"
      :class="{ show: showDetailsModal, 'd-block': showDetailsModal }"
      tabindex="-1"
      v-if="showDetailsModal"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Payslip Details - {{ selectedPayslip?.period }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeDetailsModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row mb-4">
              <div class="col-md-6">
                <p><strong>Employee:</strong> {{ user.name }}</p>
                <p><strong>Period:</strong> {{ selectedPayslip?.period }}</p>
                <p>
                  <strong>Payment Date:</strong>
                  {{ formatDate(selectedPayslip?.paymentDate) }}
                </p>
              </div>
              <div class="col-md-6">
                <p><strong>Role:</strong> {{ selectedPayslip?.role }}</p>
                <p>
                  <strong>Pay Rate:</strong>
                  {{ formatCurrency(selectedPayslip?.payRate) }}/hour
                </p>
                <p>
                  <strong>Hours Worked:</strong>
                  {{ selectedPayslip?.hoursWorked }}h
                </p>
              </div>
            </div>

            <h6 class="mb-3">Earnings</h6>
            <table class="table table-bordered mb-4">
              <tbody>
                <tr>
                  <td>
                    Regular Pay ({{ selectedPayslip?.hoursWorked }}h ×
                    {{ formatCurrency(selectedPayslip?.payRate) }})
                    <!-- A week can yield more than one payslip -->
                    <small
                      v-for="day in selectedPayslip?.days || []"
                      :key="day.date"
                      class="d-block text-muted ms-3"
                    >
                      {{ day.dayName }} {{ formatDate(day.date) }} — {{ day.hours }}h
                    </small>
                  </td>
                  <td class="text-end">
                    {{ formatCurrency(selectedPayslip?.grossPay) }}
                  </td>
                </tr>
                <tr class="table-light">
                  <td><strong>Gross Pay</strong></td>
                  <td class="text-end">
                    <strong>{{
                      formatCurrency(selectedPayslip?.grossPay)
                    }}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            <h6 class="mb-3">Deductions</h6>
            <table class="table table-bordered mb-4">
              <tbody>
                <tr v-if="selectedPayslip?.advanceRecovery > 0">
                  <td>
                    Advance Recovery
                    <div
                      v-if="
                        selectedPayslip?.advances &&
                        selectedPayslip?.advances.length > 0
                      "
                      class="small text-muted mt-1"
                    >
                      <div
                        v-for="adv in selectedPayslip.advances"
                        :key="adv.id"
                        class="ms-2"
                      >
                        • {{ adv.reason }}:
                        {{ formatCurrency(parseFloat(adv.amount)) }}
                      </div>
                    </div>
                  </td>
                  <td class="text-end text-danger">
                    -{{ formatCurrency(selectedPayslip?.advanceRecovery) }}
                  </td>
                </tr>
                <tr v-if="selectedPayslip?.otherDeductions > 0">
                  <td>Other Deductions</td>
                  <td class="text-end text-danger">
                    -{{ formatCurrency(selectedPayslip?.otherDeductions) }}
                  </td>
                </tr>
                <tr
                  v-if="
                    selectedPayslip?.advanceRecovery === 0 &&
                    selectedPayslip?.otherDeductions === 0
                  "
                >
                  <td colspan="2" class="text-center text-muted">
                    No deductions
                  </td>
                </tr>
                <tr class="table-light">
                  <td><strong>Total Deductions</strong></td>
                  <td class="text-end text-danger">
                    <strong
                      >-{{
                        formatCurrency(selectedPayslip?.deductions)
                      }}</strong
                    >
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              class="alert alert-success d-flex justify-content-between align-items-center"
            >
              <strong>Net Pay</strong>
              <h4 class="mb-0">
                {{ formatCurrency(selectedPayslip?.netPay) }}
              </h4>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeDetailsModal"
            >
              Close
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="downloadPayslip(selectedPayslip)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-download me-2"
                viewBox="0 0 16 16"
              >
                <path
                  d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"
                />
                <path
                  d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"
                />
              </svg>
              Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showDetailsModal }"
      v-if="showDetailsModal"
    ></div>
  </div>
</template>

<script>
import jsPDF from "jspdf";
import EmployeeSidebar from '@/components/EmployeeSidebar.vue';
import EmployeeNavbar from '@/components/EmployeeNavbar.vue';

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "EmployeePayslips",
  components: {
    EmployeeSidebar,
    EmployeeNavbar
  },
  data() {
    return {
      sidebarOpen: true,
      user: { name: "", email: "", role: "employee" },
      yearFilter: "all",
      showDetailsModal: false,
      selectedPayslip: null,
      payslips: [],
    };
  },
  computed: {
    filteredPayslips() {
      if (this.yearFilter === "all") {
        return this.payslips;
      }
      // Filter by year from weekEnd date
      return this.payslips.filter((p) => {
        const year = new Date(p.weekEnd).getFullYear().toString();
        return year === this.yearFilter;
      });
    },
    latestPayment() {
      return this.payslips.length > 0 ? this.payslips[0].netPay : 0;
    },
    latestPaymentDate() {
      return this.payslips.length > 0
        ? this.formatDate(this.payslips[0].paymentDate)
        : "N/A";
    },
    ytdEarnings() {
      const currentYear = new Date().getFullYear();
      return this.payslips
        .filter((p) => {
          const year = new Date(p.weekEnd).getFullYear();
          return year === currentYear;
        })
        .reduce((sum, p) => sum + p.netPay, 0);
    },
  },
  watch: {
    yearFilter() {
      this.loadPayslips();
    },
  },
  mounted() {
    const userData = localStorage.getItem("user");
    if (!userData) {
      this.$router.push("/");
      return;
    }
    this.user = JSON.parse(userData);
    if (this.user.role === "admin") {
      this.$router.push("/dashboard");
      return;
    }
    this.loadPayslips();
  },
  methods: {
    async loadPayslips() {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_URL}/api/employee/payslips?year=${this.yearFilter}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();

        if (result.success) {
          // Update user name from API if available
          if (result.data.employee.name) {
            this.user.name = result.data.employee.name;
          }

          // Transform payslips data
          this.payslips = result.data.payslips.map((p) => ({
            id: p.id,
            employeeName: this.user.name,
            period: this.formatWeekPeriod(p.weekStart, p.weekEnd),
            weekStart: p.weekStart,
            weekEnd: p.weekEnd,
            paymentDate: p.paymentDate,
            role: p.role,
            payRate: p.payRate,
            hoursWorked: p.hoursWorked,
            days: p.days || [],
            grossPay: p.grossPay,
            advanceRecovery: p.advanceRecovery,
            advances: p.advances || [],
            otherDeductions: 0,
            deductions: p.deductions,
            netPay: p.netPay,
          }));
        } else {
          alert(result.message || "Failed to load payslips");
        }
      } catch (error) {
        console.error("Load payslips error:", error);
        alert("Failed to load payslips");
      }
    },
    formatWeekPeriod(startDate, endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      const startMonth = start.toLocaleDateString("en-GB", { month: "short" });
      const startDay = start.getDate();
      const endMonth = end.toLocaleDateString("en-GB", { month: "short" });
      const endDay = end.getDate();
      const year = end.getFullYear();

      if (startMonth === endMonth) {
        return `${startMonth} ${startDay}-${endDay}, ${year}`;
      } else {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString("en-GB");
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    },
    viewDetails(payslip) {
      this.selectedPayslip = payslip;
      this.showDetailsModal = true;
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedPayslip = null;
    },
    downloadPayslip(payslip) {
      try {
        // Create new PDF document
        const doc = new jsPDF();

        // Company Header
        doc.setFillColor(44, 62, 80);
        doc.rect(0, 0, 210, 40, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont(undefined, "bold");
        doc.text("Terreverdi", 105, 20, { align: "center" });
        doc.setFontSize(12);
        doc.setFont(undefined, "normal");
        doc.text("Employee Payslip", 105, 30, { align: "center" });

        // Reset text color
        doc.setTextColor(0, 0, 0);

        // Payslip Details
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");

        let yPos = 55;

        // Employee & Period Info
        doc.setFont(undefined, "bold");
        doc.text("Employee Information", 20, yPos);
        yPos += 8;
        doc.setFont(undefined, "normal");
        doc.text(`Name: ${payslip.employeeName}`, 20, yPos);
        yPos += 6;
        doc.text(`Role: ${payslip.role}`, 20, yPos);
        yPos += 6;
        doc.text(`Pay Rate: ${this.formatCurrency(payslip.payRate)}/hour`, 20, yPos);

        yPos = 55;
        doc.setFont(undefined, "bold");
        doc.text("Payment Information", 120, yPos);
        yPos += 8;
        doc.setFont(undefined, "normal");
        doc.text(`Period: ${payslip.period}`, 120, yPos);
        yPos += 6;
        doc.text(`Payment Date: ${this.formatDate(payslip.paymentDate)}`, 120, yPos);
        yPos += 6;
        doc.text(`Hours Worked: ${payslip.hoursWorked}h`, 120, yPos);

        yPos = 95;

        // Earnings Section
        doc.setFillColor(240, 240, 240);
        doc.rect(15, yPos, 180, 8, "F");
        doc.setFont(undefined, "bold");
        doc.setFontSize(11);
        doc.text("Earnings", 20, yPos + 5.5);
        yPos += 12;

        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        doc.text(
          `Regular Pay (${payslip.hoursWorked}h × ${this.formatCurrency(
            payslip.payRate
          )})`,
          20,
          yPos
        );
        doc.text(this.formatCurrency(payslip.grossPay), 190, yPos, {
          align: "right",
        });
        yPos += 8;

        // A week can produce more than one payslip, so name the days covered
        if (payslip.days && payslip.days.length > 0) {
          doc.setFontSize(9);
          doc.setTextColor(110, 110, 110);
          for (const day of payslip.days) {
            doc.text(
              `    ${day.dayName} ${this.formatDate(day.date)} — ${day.hours}h`,
              20,
              yPos
            );
            yPos += 5;
          }
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(10);
          yPos += 3;
        }

        // Gross Pay Total
        doc.setDrawColor(200, 200, 200);
        doc.line(15, yPos, 195, yPos);
        yPos += 6;
        doc.setFont(undefined, "bold");
        doc.text("Gross Pay", 20, yPos);
        doc.text(this.formatCurrency(payslip.grossPay), 190, yPos, {
          align: "right",
        });
        yPos += 12;

        // Deductions Section
        doc.setFillColor(240, 240, 240);
        doc.rect(15, yPos, 180, 8, "F");
        doc.setFontSize(11);
        doc.text("Deductions", 20, yPos + 5.5);
        yPos += 12;

        doc.setFontSize(10);
        doc.setFont(undefined, "normal");

        if (payslip.advanceRecovery > 0) {
          doc.text("Advance Recovery", 20, yPos);
          doc.text(`-${this.formatCurrency(payslip.advanceRecovery)}`, 190, yPos, {
            align: "right",
          });
          yPos += 6;

          // Show advance details
          if (payslip.advances && payslip.advances.length > 0) {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            payslip.advances.forEach((adv) => {
              doc.text(
                `  • ${adv.reason}: ${this.formatCurrency(
                  parseFloat(adv.amount)
                )}`,
                25,
                yPos
              );
              yPos += 4;
            });
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            yPos += 2;
          }
        }

        if (payslip.otherDeductions > 0) {
          doc.text("Other Deductions", 20, yPos);
          doc.text(`-${this.formatCurrency(payslip.otherDeductions)}`, 190, yPos, {
            align: "right",
          });
          yPos += 6;
        }

        if (payslip.advanceRecovery === 0 && payslip.otherDeductions === 0) {
          doc.setTextColor(150, 150, 150);
          doc.text("No deductions", 20, yPos);
          doc.setTextColor(0, 0, 0);
          yPos += 6;
        }

        // Total Deductions
        doc.setDrawColor(200, 200, 200);
        doc.line(15, yPos, 195, yPos);
        yPos += 6;
        doc.setFont(undefined, "bold");
        doc.text("Total Deductions", 20, yPos);
        doc.text(`-${this.formatCurrency(payslip.deductions)}`, 190, yPos, {
          align: "right",
        });
        yPos += 12;

        // Net Pay Section
        doc.setFillColor(34, 139, 34);
        doc.rect(15, yPos, 180, 15, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.text("Net Pay", 20, yPos + 10);
        doc.text(this.formatCurrency(payslip.netPay), 190, yPos + 10, {
          align: "right",
        });

        // Footer
        doc.setTextColor(150, 150, 150);
        doc.setFontSize(8);
        doc.text(
          "This is a computer-generated payslip and does not require a signature.",
          105,
          280,
          { align: "center" }
        );
        doc.text(
          `Generated on ${new Date().toLocaleDateString("en-GB")}`,
          105,
          285,
          { align: "center" }
        );

        // Save PDF
        const fileName = `Payslip_${payslip.employeeName.replace(
          /\s+/g,
          "_"
        )}_${payslip.period.replace(/\s+/g, "_")}.pdf`;
        doc.save(fileName);
      } catch (error) {
        console.error("PDF generation error:", error);
        alert("Failed to generate PDF. Please try again.");
      }
    },
    handleLogout() {
      localStorage.removeItem("user");
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.sidebar {
  width: 280px;
  background-color: #2c3e50;
  color: white;
  min-height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1000;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-header h4 {
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
}

.sidebar-nav {
  padding: 1rem 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  gap: 1rem;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-left: 4px solid #667eea;
}

.main-content {
  margin-left: 280px;
  min-height: 100vh;
  background-color: #f8f9fa;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.stat-card {
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
  }
}
</style>
